"use client";

import { useRef } from "react";
import { Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Toaster } from "@/components/ui/sonner";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { toast } from "sonner";
function ReservationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    if (typeof window !== "undefined") {
      setTimeout(() => {
        toast.success(successMessage, {
          description: "Our legal team will review your details and contact you shortly."
        });
      }, 0);
    }
    return <div className="rounded-md border border-platinum-border bg-card/60 p-8">
        <p className="font-default text-2xl text-foreground font-semibold">
          Assessment Request Received.
        </p>
        <p className="mt-3 text-base text-slate-muted">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-md border-platinum-border hover:border-primary hover:text-primary">
          Submit another request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-7">
      <FormField control={control} name="full_name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-base text-foreground font-medium">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="John Alexander" className="h-11 border-0 border-b border-muted bg-transparent px-0 focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0" {...field} />
            </FormControl>
            <FormDescription className="text-xs text-slate-muted">
              Enter the legal name as it appears on your property title.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-base text-foreground font-medium">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" className="h-11 border-0 border-b border-muted bg-transparent px-0 focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-base text-foreground font-medium">
              Phone Number{" "}
              <span className="font-mono text-primary text-xs uppercase tracking-wider">
                (Direct Line)
              </span>
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 555-123-4567" className="h-11 border-0 border-b border-muted bg-transparent px-0 focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="workshop_date" rules={{
      required: "Please select your property type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-base text-foreground font-medium">
              Property Type
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value || ""}>
                <SelectTrigger className="h-11 w-full border border-muted bg-transparent px-4 focus-visible:border-primary focus-visible:ring-0">
                  <SelectValue placeholder="Select asset category" />
                </SelectTrigger>
                <SelectContent className="border-platinum-border">
                  <SelectItem value="Timeshare">Timeshare / Fractional</SelectItem>
                  <SelectItem value="Residential">Residential Property</SelectItem>
                  <SelectItem value="Commercial">Commercial Real Estate</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="experience_level" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-base text-foreground font-medium">
              Urgency Level
            </FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value || ""} className="flex flex-wrap gap-3">
                {["Immediate", "3-6 Months", "Planning"].map((level, i) => <label key={level} data-index={i} htmlFor={`exp-${level}`} className="flex cursor-pointer items-center gap-2 border border-muted bg-card/40 px-4 py-2 text-sm text-foreground transition-colors hover:border-primary has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-accent">
                    <RadioGroupItem value={level} id={`exp-${level}`} className="sr-only" />
                    {level}
                  </label>)}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="seats" rules={{
      required: "Please indicate number of units",
      min: {
        value: 1,
        message: "At least 1 unit"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-base text-foreground font-medium">
              Number of Units
            </FormLabel>
            <FormControl>
              <Input type="number" min={1} placeholder="1" className="h-11 border-0 border-b border-muted bg-transparent px-0 focus-visible:border-b-2 focus-visible:border-primary focus-visible:ring-0" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="special_requests" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-base text-foreground font-medium">
              Additional Asset Details
            </FormLabel>
            <FormControl>
              <Textarea placeholder="e.g., Resort name, location, current maintenance status, or specific legal concerns." className="min-h-24 border border-muted bg-transparent focus-visible:border-primary focus-visible:ring-0" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="newsletter_optin" render={({
      field
    }) => <FormItem className="flex flex-row items-start gap-3 space-y-0 pt-2">
            <FormControl>
              <Checkbox checked={!!field.value} onCheckedChange={field.onChange} className="mt-0.5 border-muted data-[state=checked]:bg-primary data-[state=checked]:border-primary" />
            </FormControl>
            <FormLabel className="font-default text-sm text-slate-muted leading-relaxed">
              I agree to receive market updates and legal compliance alerts regarding my property assets.
            </FormLabel>
          </FormItem>} />

      {submitError && <div className="border border-destructive/30 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="h-12 w-full bg-primary text-primary-foreground text-base font-semibold tracking-widest uppercase shadow-md transition-all hover:bg-secondary hover:text-secondary-foreground">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Processing...
          </> : "Get Your Exit Assessment"}
      </Button>
    </Form>;
}
export default function GetYourExitAssessment() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const formCard = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const footer = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="4430" id="get-your-exit-assessment" className="relative overflow-hidden bg-background text-foreground py-24 md:py-32">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -right-32 size-[600px] opacity-10 blur-3xl" style={{
      background: "radial-gradient(circle at center, var(--primary), transparent 70%)"
    }} />
      <div aria-hidden="true" className="pointer-events-none absolute -top-40 -left-40 size-[480px] opacity-5 blur-3xl" style={{
      background: "radial-gradient(circle at center, var(--secondary), transparent 70%)"
    }} />

      <div className="relative mx-auto w-full max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:gap-10 lg:gap-16">
          {}
          <div ref={intro.ref} className={`md:col-span-5 lg:col-span-4 md:pt-12 transition-all duration-1000 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <p className="font-mono text-xs font-semibold tracking-[0.2em] uppercase text-primary">
              03 / Exit Strategy
            </p>
            <h2 className="font-default text-foreground mt-6 text-3xl font-semibold leading-[1.2] md:text-4xl lg:text-5xl border-l-4 border-primary pl-6">
              Secure Your
              <br />
              Asset Liberation
              <br />
              Assessment.
            </h2>
            <p className="mt-8 max-w-sm text-lg leading-relaxed text-slate-muted font-default">
              Our legal experts provide a comprehensive review of your timeshare or fractional contract with zero upfront costs. Professional, compliant, and transparent.
            </p>

            <div className="mt-10 hidden h-px w-24 bg-platinum-border md:block" />
          </div>

          {}
          <div ref={formCard.ref} className={`md:col-span-7 lg:col-span-7 lg:col-start-6 transition-all duration-1000 delay-100 ease-out ${formCard.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="border border-platinum-border bg-card p-8 shadow-lg backdrop-blur-sm md:p-12">
              <FormProvider formKey="exit_assessment" formId="exit_assessment" sectionName="get-your-exit-assessment" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              workshop_date: "",
              experience_level: "",
              seats: "",
              special_requests: "",
              newsletter_optin: false
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              workshop_date: {
                required: true
              },
              seats: {
                required: true,
                min: 1
              }
            }} submitText="Get Your Exit Assessment" successMessage="Your request has been logged. A legal consultant will contact you within 24 hours." errorMessage="Submission failed. Please verify your details and try again.">
                <ReservationForm />
              </FormProvider>
            </div>
          </div>
        </div>

        {}
        <div ref={footer.ref} className={`mt-20 text-center transition-all duration-1000 ease-out ${footer.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <p className="font-default text-base text-slate-muted">
            Urgent inquiry? Contact our Cabo office via{" "}
            <span className="text-foreground font-mono text-sm font-semibold">
              imielitecabo@gmail.com
            </span>{" "}
            or WhatsApp at 624 164 7031.
          </p>
        </div>
      </div>

      <Toaster position="bottom-right" />
    </section>;
}